import * as React from 'react';
import styled from 'styled-components';

import {
  getDailyInspectionReport,
  getPlanReviewerTimeTrackingReport,
  ReportTableType,
} from '../../api/reports';

import { DATETIME_FORMAT, DEFAULT_DATE_FORMAT } from '../../constants';

import Table from '../../organisms/Table';

import { formatDateTime } from '../../utils/formatDate';
import tableColumnsFormatter from '../../utils/tableColumnsFormatter';
import Wrapper from './print-mixin';

const PlanReviewerTimeTrackingReport = React.forwardRef(
  (
    {
      fromDate,
      toDate,
      inspector,
      setIsValidForExport,
    }: ReportTableType,
    ref,
  ) => {
    const [
      isNullInspections,
      setIsNullInspections,
    ] = React.useState(false);

    const [totalPermits, setTotalPermits] = React.useState([]);

    const data = {
      fromDate,
      toDate,
      inspectors: inspector.map(i => i.id),
    };

    const getModifiedPlanReviewerTimeTrackingReport = () =>
      Promise.all([getPlanReviewerTimeTrackingReport(data)])
        .then(([reports]) => {
          if (reports.data?.length === 0) {
            setIsNullInspections(true);
            setIsValidForExport(false);
          } else {
            setIsNullInspections(false);
            setIsValidForExport(true);

            setTotalPermits(prevState => [...prevState, {
                title: "Total Permits",
                value: reports.data?.length,
              }]);
          }
          return {
            ...reports,
            data: {
              ...reports.data,
              results: reports.data.map(el => {
                return {
                  ...el,
                  inspector: el.inspectorName,
                  followUp: formatDateTime(el.followUp, DEFAULT_DATE_FORMAT),
                  projectNamePermitName: `${el.projectName} | ${el.permitName}`,
                };
              }),
            },
          };
        })
        .catch(err => console.log(err))
        .finally(() => {});

    const columns = column => {
      if (
        [
          'franchiseName',
          'inspectorName',
          'lastComment',
          'projectName',
          'permitName',
        ].includes(column)
      ) {
        return null;
      }

      switch (column) {
        case 'status':
          return 'Status';
        case 'tradeName':
          return 'Trade';
        case 'followUp':
          return 'Follow up Date';
        case 'activityTitle':
          return 'Activity Title';
        case 'permitNumber':
          return 'Permit Number';
        case 'projectAddress':
          return 'Permit Address';
        case 'projectNamePermitName':
          return 'Project Name | Permit Name';
        case 'inspector':
          return 'Inspector';
        default:
          return tableColumnsFormatter(column);
      }
    };

    const columnsOrder = [
      'projectNamePermitName',
      'permitNumber',
      'projectAddress',
      'inspector',
      'activityTitle',
      'tradeName',
      'status',
      'followUp',
    ];

    if (isNullInspections) {
      return (
        <InfoText>
          No project was found with selected date or
          Inspector.
        </InfoText>
      );
    }

    return (
      <Wrapper ref={ref}>
        <Title>Plan Reviewer Time Tracking Report</Title>

        <List>
          <li>
            <Bold>Inspector Name(s): </Bold>
            {inspector && inspector.length
                    ? inspector.map(i => `${i.firstName} ${i.lastName}`).join(", ")
                    : "None selected"}
          </li>
          <li>
            <Bold>Date From: </Bold> {fromDate} <Bold>To: </Bold> {toDate}
          </li>
          <li>
            <Bold>Report Date: </Bold>
            {formatDateTime(new Date(), DATETIME_FORMAT)}
          </li>
        </List>

        <Table
          customSearch
          columnsOrder={columnsOrder}
          columns={columns}
          rows={getModifiedPlanReviewerTimeTrackingReport}
          onEdit={null}
          onDelete={null}
          onConfigure={null}
          additionalActions={null}
          additionalBottomRow={totalPermits}
        />
      </Wrapper>
    );
  },
);

const List = styled.ul`
  list-style: none;
  padding: 0;
  li {
    padding: 8px 0;
    font-size: 18px;
    color: var(--primary);
  }
`;

const Bold = styled.span`
  color: var(--mainText);
  font-weight: bold;
`;
const Title = styled.h1`
  margin: 0;
`;

const InfoText = styled.div`
  margin: 0 auto;
  font-size: 24px;
  color: var(--mainText);
`;

export default PlanReviewerTimeTrackingReport;
